import { Box, Button, MenuItem, Typography } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import CustomTextField from '../../../../../../components/textfield'
import { candidateAttemptEventsWithImage } from '../../../../../../axios/api/candidate'
import { showErrorToast } from '../../../../../../utils/utils'
import { toast } from 'react-toastify'
import OverlayLoading from '../../../../../../components/overlay-loading'
import CustomDialog from '../../../../../../components/dialog'

const DETECTION_TYPES = [
  { label: 'CoderScout', value: 'coderscout' },
  { label: 'Chat GPT', value: 'chatgpt' },
  { label: 'Gemini', value: 'gemini' },
  { label: 'Google', value: 'google' }
]

const TagCaptureModal = ({
  open,
  attemptId,
  onClose,
  tagData,
  handleRefresh
}) => {
  const [detectionType, setDetectionType] = useState('')
  const [loading, setLoading] = useState(false)

  // Create object URLs for blob images
  const webcamImageUrl = useMemo(() => {
    if (tagData?.webcam?.frame instanceof Blob) {
      return URL.createObjectURL(tagData.webcam.frame)
    }
    return null
  }, [tagData?.webcam?.frame])

  const screenImageUrl = useMemo(() => {
    if (tagData?.screen?.frame instanceof Blob) {
      return URL.createObjectURL(tagData.screen.frame)
    }
    return null
  }, [tagData?.screen?.frame])

  // Clean up object URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      if (webcamImageUrl) {
        URL.revokeObjectURL(webcamImageUrl)
      }
      if (screenImageUrl) {
        URL.revokeObjectURL(screenImageUrl)
      }
    }
  }, [webcamImageUrl, screenImageUrl])

  const handleSave = useCallback(async () => {
    setLoading(true)
    const newData = { ...tagData }

    const formData = new FormData()

    formData.append('detectionType', detectionType)
    formData.append('eventTimestamp', newData?.timestamp || '')

    if (newData?.webcam?.frame) {
      const filename = `proof-webcam-${Date.now()}.jpg`
      formData.append('webcamImage', newData?.webcam?.frame, filename)
      delete newData.webcam.frame
    }

    if (newData?.screen?.frame) {
      const filename = `proof-screen-${Date.now()}.jpg`
      formData.append('screenShareImage', newData?.screen?.frame, filename)
      delete newData.screen.frame
    }

    formData.append('webcam', JSON.stringify(newData?.webcam))
    formData.append('screen', JSON.stringify(newData?.screen))

    const response = await candidateAttemptEventsWithImage(
      attemptId,
      'developerTag',
      formData
    )
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Tag added successfully')
      onClose()
      if (handleRefresh) {
        handleRefresh()
      }
    }
  }, [attemptId, detectionType, onClose, tagData, handleRefresh])

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }, [])

  return (
    <>
      {open && loading && <OverlayLoading />}
      <CustomDialog
        open={open}
        handleClose={onClose}
        title="Add Tag - Captured Information"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Detection Type Dropdown */}
          <CustomTextField
            select
            fullWidth
            required
            label="Detection Type"
            value={detectionType}
            onChange={(e) => setDetectionType(e.target.value)}
          >
            {DETECTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </CustomTextField>

          {/* Timestamp */}
          {tagData?.timestamp && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Timestamp
              </Typography>
              <Typography variant="body1">
                {new Date(tagData.timestamp).toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* Webcam Data */}
          {tagData?.webcam && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography variant="h6" gutterBottom>
                Webcam Recording
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Filename
                  </Typography>
                  <Typography variant="body2">
                    {tagData.webcam.filename || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(tagData.webcam.duration)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Time
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(tagData.webcam.currentTime)}
                  </Typography>
                </Box>
              </Box>

              {webcamImageUrl && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Captured Frame
                  </Typography>
                  <img
                    src={webcamImageUrl}
                    alt="Webcam capture"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Screen Share Data */}
          {tagData?.screen && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Typography variant="h6" gutterBottom>
                Screen Recording
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Filename
                  </Typography>
                  <Typography variant="body2">
                    {tagData.screen.filename || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(tagData.screen.duration)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Time
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(tagData.screen.currentTime)}
                  </Typography>
                </Box>
              </Box>

              {screenImageUrl && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Captured Frame
                  </Typography>
                  <img
                    src={screenImageUrl}
                    alt="Screen capture"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Actions */}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}
          >
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!detectionType}
            >
              Save Tag
            </Button>
          </Box>
        </Box>
      </CustomDialog>
    </>
  )
}

export default memo(TagCaptureModal)
